import type { InfiniteData, QueryClient, QueryKey } from '@tanstack/react-query';
import type { Page } from '@vmc/shared';
import { useCallback, useSyncExternalStore } from 'react';

/*
 * Rows deleted inside their undo window still exist on the server, so any refetch in those seconds
 * (opening the list page, a live update) would bring them back. Instead of editing cached data,
 * lists filter through the set of hidden ids (a query `select`), and re-render whenever it changes.
 * The cache keeps the real server data, so Undo only has to un-hide.
 */
let hidden: ReadonlySet<string> = new Set();
const listeners = new Set<() => void>();

function setHidden(next: ReadonlySet<string>) {
  hidden = next;
  for (const l of listeners) l();
}
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/** The ids hidden right now; the component re-renders when it changes. */
export const useHidden = (): ReadonlySet<string> => useSyncExternalStore(subscribe, () => hidden);

export const withoutHidden = <T extends { id: string }>(items: T[], h: ReadonlySet<string>): T[] =>
  h.size ? items.filter((i) => !h.has(i.id)) : items;

/** `select` for a plain list query. */
export function useVisibleSelect<T extends { id: string }>(): (items: T[]) => T[] {
  const h = useHidden();
  return useCallback((items: T[]) => withoutHidden(items, h), [h]);
}

/** `select` for a paginated (infinite) list query. */
export function useVisiblePagesSelect<T extends { id: string }>(): (
  d: InfiniteData<Page<T>>,
) => InfiniteData<Page<T>> {
  const h = useHidden();
  return useCallback(
    (d: InfiniteData<Page<T>>) =>
      h.size
        ? { ...d, pages: d.pages.map((p) => ({ ...p, items: withoutHidden(p.items, h) })) }
        : d,
    [h],
  );
}

export interface HiddenRow {
  /** Undo: show the row again. */
  restore: () => void;
  /** The delete went through: wait for `refetch` to drop it from the cache, then forget it. */
  settle: (refetch: () => Promise<unknown>) => Promise<void>;
}

/** Hides one row everywhere it's listed, for a delete that's inside its undo window. */
export function hideRow(id: string): HiddenRow {
  setHidden(new Set([...hidden, id]));
  const unhide = () => {
    const next = new Set(hidden);
    next.delete(id);
    setHidden(next);
  };
  return {
    restore: unhide,
    settle: async (refetch) => {
      await refetch().catch(() => undefined);
      unhide();
    },
  };
}

/**
 * Optimistic in-place edit of one cached query (DESIGN.md rule 2): cancels its in-flight fetches
 * so they can't overwrite the edit, applies `update`, and returns a rollback that restores the
 * previous data. Call it from a mutation's `onMutate` and run the rollback in `onError`.
 */
export async function patchQuery<T>(
  qc: QueryClient,
  key: QueryKey,
  update: (old: T | undefined) => T | undefined,
): Promise<() => void> {
  await qc.cancelQueries({ queryKey: key });
  const before = qc.getQueryData<T>(key);
  qc.setQueryData<T>(key, update);
  return () => qc.setQueryData<T>(key, before);
}
